/**
 * StudentClassDetailPage
 *
 * Per-class view for a student: Sessions (read-only) + Broadcasts tabs.
 * Reached from /my-classes/:offeringId.
 * Roster is intentionally excluded — students cannot see who else is enrolled.
 */

import { useParams, Link } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  AlertCircle, ArrowLeft, BarChart2, BookOpen, CalendarDays, CheckCircle2,
  Mail, MessageSquare, Minus, XCircle,
} from "lucide-react";
import { StudentLayout } from "@/components/student/layout/StudentLayout";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useMyClasses, useMyClassAttendance, useStudentClassSessions, useStudentClassBroadcasts } from "@/hooks/use-class-management";
import type { SessionAttendanceItem } from "@/services/classManagementService";
import { useQuery } from "@tanstack/react-query";
import { getPublicOfferingById } from "@/services/teacherProfileService";
import { ASSESSMENT_BADGE } from "@/components/teacher/offering/MilestonesBuilder";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatSessionDate(iso: string) {
  const d = new Date(iso);
  return {
    weekday: d.toLocaleDateString([], { weekday: "short" }),
    date:    d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }),
    time:    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    dayNum:  d.getDate(),
  };
}

function durationLabel(m: number) {
  return m < 60 ? `${m} min` : `${m / 60}h${m % 60 ? ` ${m % 60}m` : ""}`;
}

function formatRelative(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

// ── Attendance Tab ────────────────────────────────────────────────────────────

function attendanceIcon(status: string) {
  if (status === "PRESENT") return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" aria-hidden />;
  if (status === "ABSENT")  return <XCircle className="w-3.5 h-3.5 text-red-400" aria-hidden />;
  if (status === "LATE")    return <AlertCircle className="w-3.5 h-3.5 text-amber-400" aria-hidden />;
  return <Minus className="w-3.5 h-3.5 text-slate-300" aria-hidden />;
}

function attendanceLabel(status: string) {
  if (status === "PRESENT") return "Present";
  if (status === "ABSENT")  return "Absent";
  if (status === "LATE")    return "Late";
  return "Not marked";
}

function attendanceTextClass(status: string) {
  if (status === "PRESENT") return "text-emerald-700";
  if (status === "ABSENT")  return "text-red-500";
  if (status === "LATE")    return "text-amber-600";
  return "text-slate-400";
}

function AttendanceTab({ offeringId }: { offeringId: string }) {
  const { data, isLoading, isError } = useMyClassAttendance(offeringId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-12 bg-white rounded-xl border border-slate-200 animate-pulse" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
        Could not load attendance. Please try again.
      </div>
    );
  }

  if (!data || data.sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-14 bg-white rounded-2xl border border-slate-200">
        <BarChart2 className="w-8 h-8 text-slate-300 mb-3" aria-hidden />
        <p className="text-sm font-semibold text-slate-700 mb-1">No attendance recorded yet</p>
        <p className="text-xs text-slate-400">Your teacher marks attendance after each session.</p>
      </div>
    );
  }

  const attended = data.attended;
  const total    = data.attended + data.absent + data.late;
  const pct      = total > 0 ? Math.round((attended / total) * 100) : null;

  return (
    <div className="space-y-5">
      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-3">
        {([
          { label: "Attended", count: data.attended,  cls: "text-emerald-700 bg-emerald-50 border-emerald-100" },
          { label: "Absent",   count: data.absent,    cls: "text-red-500 bg-red-50 border-red-100" },
          { label: "Late",     count: data.late,      cls: "text-amber-600 bg-amber-50 border-amber-100" },
          { label: "Unmarked", count: data.notMarked, cls: "text-slate-500 bg-slate-50 border-slate-100" },
        ] as const).map(({ label, count, cls }) => (
          <div key={label} className={cn("rounded-xl border p-3 text-center", cls)}>
            <p className="text-lg font-bold">{count}</p>
            <p className="text-xs font-medium">{label}</p>
          </div>
        ))}
      </div>

      {pct !== null && (
        <p className="text-sm text-slate-500">
          Attendance rate:{" "}
          <span className={cn(
            "font-semibold",
            pct >= 75 ? "text-emerald-700" : "text-amber-600",
          )}>
            {pct}%
          </span>
        </p>
      )}

      {/* Session-level list */}
      <div className="space-y-1.5">
        {(data.sessions as SessionAttendanceItem[]).map((s) => (
          <div
            key={s.sessionId}
            className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3"
          >
            <span className="text-sm text-slate-700 min-w-0 truncate">
              {formatRelative(s.scheduledStart)}
            </span>
            <span className={cn("flex items-center gap-1.5 text-xs font-medium shrink-0", attendanceTextClass(s.status))}>
              {attendanceIcon(s.status)}
              {attendanceLabel(s.status)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Sessions Tab ──────────────────────────────────────────────────────────────

function SessionsTab({ offeringId, summaries, totalSessions }: { offeringId: string; summaries: { sessionId: string; body: string; sentAt: string }[]; totalSessions?: number }) {
  const { data, isLoading, isError } = useStudentClassSessions(offeringId);
  const summaryMap = Object.fromEntries(summaries.map((s) => [s.sessionId, s]));

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-16 bg-white rounded-xl border border-slate-200 animate-pulse" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
        Could not load sessions. Please try again.
      </div>
    );
  }

  const sessions = data ?? [];
  const now = new Date();
  const upcoming  = sessions.filter((s) => s.status !== "CANCELLED" && new Date(s.scheduledStart) >= now);
  const past      = sessions.filter((s) => s.status !== "CANCELLED" && new Date(s.scheduledStart) <  now).reverse();
  const cancelled = sessions.filter((s) => s.status === "CANCELLED");

  if (sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-14 bg-white rounded-2xl border border-slate-200">
        <CalendarDays className="w-8 h-8 text-slate-300 mb-3" aria-hidden />
        <p className="text-sm font-semibold text-slate-700 mb-1">No sessions scheduled yet</p>
        <p className="text-xs text-slate-400">Your teacher will schedule sessions soon.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        <span className="font-semibold text-slate-800">{upcoming.length}</span> upcoming ·{" "}
        <span className="font-semibold text-slate-800">{past.length}</span> past
        {totalSessions ? (
          <span className="ml-2 text-slate-400">
            · <span className="font-semibold text-slate-600">{past.length}</span> / {totalSessions} done
          </span>
        ) : null}
      </p>

      {upcoming.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
            Upcoming ({upcoming.length})
          </p>
          {upcoming.map((session) => {
            const { weekday, date, time, dayNum } = formatSessionDate(session.scheduledStart);
            return (
              <div
                key={session.id}
                className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4"
              >
                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex flex-col items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-indigo-400 uppercase leading-none">{weekday}</span>
                  <span className="text-sm font-bold text-indigo-700 leading-tight">{dayNum}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{date}</p>
                  <p className="text-xs text-slate-500">{time} · {durationLabel(session.durationMinutes)}</p>
                </div>
                <Badge className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-50 shrink-0">
                  Upcoming
                </Badge>
              </div>
            );
          })}
        </div>
      )}

      {past.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
            Past sessions ({past.length})
          </p>
          {past.map((session) => {
            const { weekday, date, time, dayNum } = formatSessionDate(session.scheduledStart);
            const summary = summaryMap[session.id];
            return (
              <div
                key={session.id}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden"
              >
                <div className="flex items-center gap-4 p-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex flex-col items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-slate-400 uppercase leading-none">{weekday}</span>
                    <span className="text-sm font-bold text-slate-600 leading-tight">{dayNum}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-700">{date}</p>
                    <p className="text-xs text-slate-400">{time} · {durationLabel(session.durationMinutes)}</p>
                  </div>
                </div>
                {summary && (
                  <div className="border-t border-slate-100 px-4 py-3 bg-slate-50">
                    <p className="text-xs text-slate-400 mb-1">Session summary</p>
                    <p className="text-sm text-slate-700 leading-relaxed">{summary.body}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {cancelled.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
            Cancelled ({cancelled.length})
          </p>
          {cancelled.map((session) => {
            const { weekday, date, time, dayNum } = formatSessionDate(session.scheduledStart);
            return (
              <div
                key={session.id}
                className="flex items-center gap-4 bg-slate-50 border border-slate-200 rounded-xl p-4 opacity-60"
              >
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex flex-col items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-slate-400 uppercase leading-none line-through">{weekday}</span>
                  <span className="text-sm font-bold text-slate-400 leading-tight">{dayNum}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-500 line-through">{date}</p>
                  <p className="text-xs text-slate-400">{time} · {durationLabel(session.durationMinutes)}</p>
                </div>
                <Badge className="text-xs bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-100 shrink-0">
                  Cancelled
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Broadcasts Tab ────────────────────────────────────────────────────────────

function BroadcastsTab({ offeringId }: { offeringId: string }) {
  const { data, isLoading, isError } = useStudentClassBroadcasts(offeringId);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 space-y-2 animate-pulse">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
        Could not load messages. Please try again.
      </div>
    );
  }

  const broadcasts = data ?? [];

  if (broadcasts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-14 bg-white rounded-2xl border border-slate-200">
        <MessageSquare className="w-8 h-8 text-slate-300 mb-3" aria-hidden />
        <p className="text-sm font-semibold text-slate-700 mb-1">No messages yet</p>
        <p className="text-xs text-slate-400">Your teacher's broadcast messages will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {broadcasts.map((broadcast) => (
        <div
          key={broadcast.id}
          className="bg-white border border-slate-200 rounded-xl p-4 space-y-2 hover:border-slate-300 transition-colors"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-indigo-500 shrink-0 mt-0.5" aria-hidden />
              <p className="text-sm font-semibold text-slate-900">{broadcast.subject}</p>
            </div>
            <span className="text-xs text-slate-400 shrink-0">{formatRelative(broadcast.sentAt)}</span>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed pl-5">{broadcast.body}</p>
        </div>
      ))}
    </div>
  );
}

// ── Curriculum Tab ────────────────────────────────────────────────────────────

function CurriculumTab({ offeringId, sessionsDone }: { offeringId: string; sessionsDone: number }) {
  const { data: offering, isLoading } = useQuery({
    queryKey: ["publicOffering", offeringId],
    queryFn: () => getPublicOfferingById(offeringId),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-16 bg-white rounded-xl border border-slate-200 animate-pulse" />
        ))}
      </div>
    );
  }

  const milestones: any[] =
    (offering as any)?.group?.curriculumMilestones ??
    (offering as any)?.group?.milestones ??
    (offering as any)?.crashCourse?.milestones ??
    [];

  const sessionsPerWeek: number =
    (offering as any)?.group?.sessionsPerWeek ??
    (offering as any)?.crashCourse?.sessionsPerWeek ??
    1;

  const currentWeek = Math.floor(sessionsDone / sessionsPerWeek) + 1;

  if (!milestones.length) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-14 bg-white rounded-2xl border border-slate-200">
        <BookOpen className="w-8 h-8 text-slate-300 mb-3" aria-hidden />
        <p className="text-sm font-semibold text-slate-700 mb-1">No curriculum plan yet</p>
        <p className="text-xs text-slate-400">Your teacher hasn't added a week-by-week plan yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Week {Math.min(currentWeek, milestones.length)} of {milestones.length} — based on sessions completed
      </p>
      {milestones.map((m: any, i: number) => {
        const weekNum = m.weekNumber ?? i + 1;
        const isDone    = weekNum < currentWeek;
        const isCurrent = weekNum === currentWeek;
        const badge = m.assessmentType && m.assessmentType !== "NONE"
          ? ASSESSMENT_BADGE[m.assessmentType as keyof typeof ASSESSMENT_BADGE]
          : null;
        return (
          <div
            key={i}
            className={cn(
              "flex items-start gap-3 p-4 rounded-xl border transition-colors",
              isDone    ? "bg-emerald-50 border-emerald-200" :
              isCurrent ? "bg-indigo-50 border-indigo-300 shadow-sm" :
                          "bg-white border-slate-200",
            )}
          >
            <div className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0",
              isDone    ? "bg-emerald-500 text-white" :
              isCurrent ? "bg-indigo-600 text-white" :
                          "bg-slate-100 text-slate-500",
            )}>
              {isDone ? <CheckCircle2 className="w-4 h-4" /> : weekNum}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className={cn(
                  "text-sm font-semibold",
                  isDone ? "text-emerald-800" : isCurrent ? "text-indigo-900" : "text-slate-700",
                )}>
                  {m.title || `Week ${weekNum}`}
                </p>
                {isCurrent && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                    Current
                  </span>
                )}
                {badge && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                    {badge}
                  </span>
                )}
              </div>
              {m.description && (
                <p className="text-xs text-slate-500 mt-0.5">{m.description}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function StudentClassDetailPage() {
  const { offeringId = "" } = useParams<{ offeringId: string }>();

  // Resolve title + session count from the hub cache — no extra fetch
  const { data: allClasses } = useMyClasses();
  const enrollment = allClasses?.find((c) => c.offeringId === offeringId);
  const title = enrollment?.title ?? "Class";

  usePageTitle(enrollment ? `${title} | BookMyCoaching` : "Class | BookMyCoaching");

  const { data: sessions } = useStudentClassSessions(offeringId);
  const { data: broadcasts } = useStudentClassBroadcasts(offeringId);
  const { data: attendance } = useMyClassAttendance(offeringId);

  const sessionCount    = sessions?.length ?? 0;
  const broadcastCount  = broadcasts?.length ?? 0;
  const attendanceTotal = attendance ? attendance.attended + attendance.absent + attendance.late : 0;

  const offeringType = enrollment?.offeringType;
  const hasCurriculum = offeringType === "GROUP" || offeringType === "CRASH_COURSE";
  const now = new Date();
  const sessionsDone = (sessions ?? []).filter(
    (s: any) => s.status !== "CANCELLED" && new Date(s.scheduledStart) < now,
  ).length;
  const totalSessions = enrollment?.totalSessions;

  return (
    <StudentLayout>
      {/* Header */}
      <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100 shrink-0 bg-white">
        <Link
          to="/my-classes"
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 transition-colors duration-150 mb-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden />
          My classes
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 leading-tight">{title}</h1>
        {enrollment?.teacherName && (
          <p className="text-sm text-slate-500 mt-0.5">with {enrollment.teacherName}</p>
        )}
      </div>

      <main className="flex-1 overflow-auto bg-slate-50">
        <div className="px-4 sm:px-6 py-6 max-w-3xl">
          <Tabs defaultValue="sessions" className="w-full">
            <TabsList className={cn(
              "bg-white border border-slate-200 rounded-xl p-1 h-auto gap-1 mb-5",
            )}>
              <TabsTrigger
                value="sessions"
                className={cn(
                  "cursor-pointer rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-150",
                  "data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-sm",
                  "data-[state=inactive]:text-slate-600 data-[state=inactive]:hover:text-slate-900",
                )}
              >
                <CalendarDays className="w-3.5 h-3.5 mr-1.5" aria-hidden />
                Sessions
                {sessionCount > 0 && (
                  <span className="ml-1.5 text-xs font-semibold opacity-70">{sessionCount}</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="broadcasts"
                className={cn(
                  "cursor-pointer rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-150",
                  "data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-sm",
                  "data-[state=inactive]:text-slate-600 data-[state=inactive]:hover:text-slate-900",
                )}
              >
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" aria-hidden />
                Messages
                {broadcastCount > 0 && (
                  <span className="ml-1.5 text-xs font-semibold opacity-70">{broadcastCount}</span>
                )}
              </TabsTrigger>
              {hasCurriculum && (
                <TabsTrigger
                  value="curriculum"
                  className={cn(
                    "cursor-pointer rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-150",
                    "data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-sm",
                    "data-[state=inactive]:text-slate-600 data-[state=inactive]:hover:text-slate-900",
                  )}
                >
                  <BookOpen className="w-3.5 h-3.5 mr-1.5" aria-hidden />
                  Curriculum
                </TabsTrigger>
              )}
              <TabsTrigger
                value="attendance"
                className={cn(
                  "cursor-pointer rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-150",
                  "data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-sm",
                  "data-[state=inactive]:text-slate-600 data-[state=inactive]:hover:text-slate-900",
                )}
              >
                <BarChart2 className="w-3.5 h-3.5 mr-1.5" aria-hidden />
                Attendance
                {attendanceTotal > 0 && (
                  <span className="ml-1.5 text-xs font-semibold opacity-70">{attendanceTotal}</span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sessions">
              <SessionsTab offeringId={offeringId} summaries={enrollment?.summaries ?? []} totalSessions={totalSessions} />
            </TabsContent>
            {hasCurriculum && (
              <TabsContent value="curriculum">
                <CurriculumTab offeringId={offeringId} sessionsDone={sessionsDone} />
              </TabsContent>
            )}
            <TabsContent value="broadcasts">
              <BroadcastsTab offeringId={offeringId} />
            </TabsContent>
            <TabsContent value="attendance">
              <AttendanceTab offeringId={offeringId} />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </StudentLayout>
  );
}
